// template-list.tsx — the Templates tab's MANAGEMENT list.
//
// Distinct from `template-picker.tsx` (an in-wizard SELECTOR with a live
// preview, "Use template" applies it into the editor and closes). This is the
// list of saved templates themselves: search, see name + snippet + media/poll/
// link badges, and act on each row (Edit / Duplicate / Delete) via the shared
// `ActionsMenu`. Edit delegates to the REAL template editor through the
// redesign's nav context — picking here never opens an inline editor.
//
// Visual language matches the rest of the Templates/Campaigns/Audiences tabs
// (centered `CARD_COLUMN_MAX` column, `.campaigns-rd` CSS-var tokens, `.btn`/
// `.inp` utility classes) so it reads as one system, not a bolted-on legacy
// view.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, Paperclip, Link as LinkIcon, BarChart3, Pencil, Copy, Trash2, MessageSquareText, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBTemplateThunks, TEMPLATE_LIST_QUERY, templateSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';
import { ActionsMenu, type ActionItem } from '@extension/context/content/components/campaigns-redesign/action-menu';
import { ListSkeleton } from '@extension/context/content/components/campaigns-redesign/primitives';
import { FirstRunEmptyState } from '@extension/context/content/components/campaigns-redesign/empty-state';
import { CampaignMessageBubble, templateToPreviewMessage, isEmptyPreview } from '@extension/context/content/components/campaigns-redesign/campaign-message-bubble';
import type { Template } from '@common/models/template';
import { namespace } from '@common/config/constants';

const CARD_COLUMN_MAX = 1040;

// The soft-out applied to a preview that is taller than its 210px cap. Only
// the last ~52px are faded, and only when the content is actually clipped —
// an always-on mask would dim the tail of every short message for no reason.
const PREVIEW_FADE_MASK = 'linear-gradient(180deg, #000 calc(100% - 52px), rgba(0,0,0,0) 100%)';

/**
 * How many cards are RENDERED at a time.
 *
 * The fetch is not the bottleneck — `TEMPLATE_LIST_QUERY` already pulls the
 * whole account in one call (see the `TEMPLATE_LIST_LIMIT` note; the busiest
 * prod account has 1,343 templates, well under the cap). What hurts is
 * MOUNTING them: every card renders a full WhatsApp preview bubble and wires a
 * ResizeObserver, so a big account paid for its entire library on tab-open.
 *
 * So the list renders one page, then grows a page at a time as the user
 * reaches the bottom (auto via IntersectionObserver, plus an explicit
 * "Load more" button so it never depends on the observer firing). Paging is
 * client-side ON PURPOSE: search must match the whole library, not just the
 * cards that happen to be on screen.
 */
export const TEMPLATE_PAGE_SIZE = 30;

/** Pure slice helper — the paging math, testable without a DOM. */
export function pageTemplates<T>(list: T[], visibleCount: number) {
  const safeCount = Math.max(0, visibleCount);
  const visible = list.slice(0, safeCount);
  return {
    visible,
    remaining: Math.max(0, list.length - visible.length),
    hasMore: list.length > visible.length,
  };
}

const TemplateRow: React.FC<{ tpl: Template }> = ({ tpl }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const nav = useCampaignsRedesignNav();

  const id = String(tpl._id ?? '');
  const hasFile = !!tpl.asset?.url;
  const hasLink = !!tpl.linkPreview?.url;
  const isPoll = !!tpl.isPoll;

  // Whole-row Edit: opening the real template editor via the redesign nav —
  // the SAME action the "…" menu's Edit item fires.
  const openEditor = () => nav?.openTemplate(id);

  const previewMessage = templateToPreviewMessage(tpl);
  const showPreview = !isEmptyPreview(previewMessage);

  const actionItems: ActionItem[] = [
    {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: Pencil,
      onSelect: openEditor,
    },
    {
      key: 'duplicate',
      label: t('common.duplicate', 'Duplicate'),
      icon: Copy,
      onSelect: () => {
        const { _id: _ignored, ...templateWithoutId } = tpl;
        dispatch(
          DBTemplateThunks.create({
            ...templateWithoutId,
            name: (tpl.name ?? t('templates.unnamed', 'Unnamed Template')) + t('campaignsRedesign.copySuffix', ' (copy)'),
          }),
        );
      },
    },
    {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: Trash2,
      danger: true,
      confirm: true,
      onSelect: () => {
        dispatch(DBTemplateThunks.patch({ entity: { ...tpl, deleted: true } }));
      },
    },
  ];

  // Clipped-preview detection: the preview panel hard-caps its height with
  // overflow hidden (NO scrollbar — a gallery card shouldn't scroll), and when
  // the bubble is actually taller than the cap we soften the cut (mask fade +
  // chevron). ResizeObserver keeps the flag honest when async content (images,
  // link previews) inflates the bubble after mount.
  const previewRef = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const check = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);

  // One cohesive gallery card: compact header (name + badges + menu) over a
  // chat-styled preview panel holding the WA bubble. `breakInside: avoid`
  // keeps the card whole inside the masonry columns; inline-block + width
  // 100% is the combo that makes column fragmentation honor it reliably.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openEditor}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openEditor();
        }
      }}
      style={{
        breakInside: 'avoid',
        display: 'inline-block',
        width: '100%',
        verticalAlign: 'top',
        marginBottom: 10,
        cursor: 'pointer',
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--surface)',
        overflow: 'hidden',
        transition: 'border-color .12s, box-shadow .12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--line-strong)';
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(11,18,32,.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--line)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBlock: 7, paddingInlineStart: 12, paddingInlineEnd: 6 }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 600,
            color: 'var(--ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {tpl.name || t('templates.unnamed', 'Unnamed Template')}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted-2)', flexShrink: 0 }}>
          {isPoll && <BarChart3 size={13} />}
          {hasFile && <Paperclip size={13} />}
          {hasLink && !hasFile && <LinkIcon size={13} />}
        </div>
        {/* stopPropagation so opening/using the "…" menu never also triggers the
            card's edit-on-click. */}
        <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <ActionsMenu items={actionItems} />
        </div>
      </div>
      {/* Chat-styled preview panel: the shared CampaignMessageBubble on a
          muted surface (reads as a WhatsApp conversation, and visually ties
          the bubble to its card). Hard height cap, overflow hidden, and a
          soft-out when clipped — the full message lives one click away in the
          editor. */}
      {showPreview ? (
        <div style={{ background: 'var(--surface-3)', borderTop: '1px solid var(--line)' }}>
          {/* Flush, full-bleed message: zero padding and a full-width bubble —
              the panel effectively IS the message. The bubble's tail triangle
              (7px past its right edge) gets clipped by overflow hidden, which
              just leaves the bubble's already-square top-right corner.

              The clip is softened with a MASK, not an overlaid gradient. The
              overlay version painted a hard-coded fade to `--surface-3` on top
              of whatever was underneath — over the green WhatsApp bubble (and
              over photo/link previews) that read as a white smear across the
              message, and it would have been the wrong colour outright in dark
              mode. A mask fades the content's own pixels to transparent, so it
              is correct over text, bubble and image alike, in any theme. */}
          <div
            ref={previewRef}
            style={{
              maxHeight: 210,
              overflow: 'hidden',
              ...(clipped ? { maskImage: PREVIEW_FADE_MASK, WebkitMaskImage: PREVIEW_FADE_MASK } : {}),
            }}
          >
            <CampaignMessageBubble message={previewMessage} align="end" fullWidth />
          </div>
          {/* A quiet "there is more" affordance under the fade. Icon only and
              deliberately unlabeled: the card's click opens the EDITOR, so any
              "read more" wording would promise a reader this list doesn't have. */}
          {clipped && (
            <div
              aria-hidden
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 20,
                color: 'var(--muted-2)',
                opacity: 0.75,
                pointerEvents: 'none',
              }}
            >
              <ChevronDown size={13} />
            </div>
          )}
        </div>
      ) : (
        /* The snippet only appears as a FALLBACK when there's no preview bubble
           — with a bubble the snippet would just duplicate the bubble's text. */
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            background: 'var(--surface-2)',
            borderTop: '1px solid var(--line)',
            padding: '10px 14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {tpl.message || (isPoll ? t('campaign.poll', 'Poll') : t('templates.unnamed', 'Unnamed Template'))}
        </div>
      )}
    </div>
  );
};

export const TemplateList: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const nav = useCampaignsRedesignNav();

  const templates = useAppSelector((state) =>
    templateSelectByQuery(state.DBTemplateReducer, { deleted: false }, [], { updatedAt: -1 }),
  ) as Template[];
  // First-load flag: show the skeleton until the templates collection has
  // resolved once, so a cold reload doesn't flash the empty state before rows.
  const itemsLoaded = useAppSelector((state) => state.DBTemplateReducer.itemsLoaded);

  useEffect(() => {
    dispatch(DBTemplateThunks.find({ deleted: false, ...TEMPLATE_LIST_QUERY }));
  }, [dispatch]);

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (tpl) =>
        (tpl.name ?? '').toLowerCase().includes(q) ||
        (tpl.message ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  // Render-paging. `visibleCount` resets whenever the result set the user is
  // looking at changes (a new search term is a new list — starting it half
  // scrolled-through would be nonsense).
  const [visibleCount, setVisibleCount] = useState(TEMPLATE_PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(TEMPLATE_PAGE_SIZE);
  }, [search]);

  const { visible, remaining, hasMore } = useMemo(
    () => pageTemplates(filtered, visibleCount),
    [filtered, visibleCount],
  );

  const loadMore = () => setVisibleCount((c) => c + TEMPLATE_PAGE_SIZE);

  // Infinite scroll: a sentinel below the last card. `rootMargin` starts the
  // next page BEFORE it is on screen so the growth feels continuous rather
  // than "scroll, stop, wait". `root: null` still works inside the extension
  // panel's own scroller — an ancestor scroll container clips the sentinel out
  // of the viewport intersection, which is exactly the signal we want.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visibleCount, filtered.length]);

  // First-run vs "loaded but filtered to nothing". Only the FIRST hides the
  // toolbar: with zero templates there is nothing to search, so the search box
  // is noise standing between the user and the one button that matters.
  const isFirstRun = itemsLoaded && templates.length === 0;

  return (
    <div style={{ padding: '8px 24px 24px', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
      {/* header: search + new template */}
      {!isFirstRun && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search
            size={14}
            style={{ position: 'absolute', insetInlineStart: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-2)', pointerEvents: 'none' }}
          />
          <input
            className="inp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('templates.searchPlaceholder', 'Search templates')}
            style={{ paddingInlineStart: 32 }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn primary" onClick={() => nav?.newTemplate()} disabled={!nav}>
          <Plus size={14} /> {t('templates.newTemplate', 'New template')}
        </button>
      </div>
      )}

      {/* list / loading / empty states */}
      {!itemsLoaded && templates.length === 0 ? (
        <ListSkeleton />
      ) : isFirstRun ? (
        <FirstRunEmptyState
          icon={MessageSquareText}
          headline={t('templates.emptyHeadline', 'Create your first template')}
          body={t(
            'templates.emptyBody',
            'A template is a message you save once — text, media or a poll — and reuse in any campaign or scheduled message.',
          )}
          ctaLabel={t('templates.newTemplate', 'New template')}
          onCta={() => nav?.newTemplate()}
          ctaDisabled={!nav}
        />
      ) : filtered.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '56px 24px',
            textAlign: 'center',
            color: 'var(--muted)',
          }}
        >
          <MessageSquareText size={26} style={{ opacity: 0.5 }} />
          <span style={{ fontSize: 13 }}>
            {t('templates.noResults', 'No templates match your search')}
          </span>
        </div>
      ) : (
        /* Masonry-style flow via CSS multi-columns — tiles pack under each
           other at their natural heights instead of aligning to strict grid
           rows. columnWidth decides how many columns fit; each tile guards
           itself with breakInside: avoid. */
        <>
          <div style={{ columnWidth: 290, columnGap: 10 }}>
            {visible.map((tpl) => (
              <TemplateRow key={String(tpl._id ?? '')} tpl={tpl} />
            ))}
          </div>
          {/* Sentinel + explicit button. The button is not a fallback for
              looks: if the observer is unavailable (or a layout leaves the
              sentinel permanently off-screen) this is still a reachable way to
              see the rest of the library. */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {hasMore && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '18px 0 4px',
              }}
            >
              <button type="button" className="btn" onClick={loadMore}>
                {t('templates.loadMore', 'Load more')}
              </button>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                {t('templates.shownOfTotal', '{{shown}} of {{total}}', {
                  shown: visible.length,
                  total: filtered.length,
                  remaining,
                })}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateList;
